/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import Keycloak from 'keycloak-js';
import { AWSKeycloakService } from './AWSKeycloakService';
import TenantAPI from 'api/TenantAPI';
import { getBiteLoginRedirectUri } from 'utils/teamOnboarding';
import {
  withRecoveryDraftsPreserved,
  markForcedNavigation,
} from './sessionRecovery';

export const KEYCLOAK_URL =
  process.env.REACT_APP_KEYCLOAK_URL || //eslint-disable-line
  'https://keycloak-sandbox.nubotools.com/auth';

let _kc = new Keycloak({
  url: KEYCLOAK_URL,
  realm: 'BITE',
  clientId: 'bite_client_local',
});

_kc.onTokenExpired = () => {
  updateToken().catch((err) => {
    console.warn(
      'Background refresh failed (micro-disconnect or sleep mode). Leaving session intact.',
      err,
    );
  });
};

let isAWSUserTokenValid = null;
let isJwtLoginSession = false;
let aWSKeycloakService = new AWSKeycloakService();
// eslint-disable-next-line
const BITE_DOMAINS = process.env.REACT_APP_BITE_DOMAINS;
// eslint-disable-next-line
const JWT_STORAGE_KEY = 'bite.jwt.sessionToken';
// eslint-disable-next-line
const DOMAIN_STORAGE_KEY = 'bite.jwt.domain';
const DOMAIN_STORAGE_FALLBACK = 'bite.jwt.fallback';
const KC_ACCESS_TOKEN_KEY = 'bite.kc.accessToken';
const KC_LEGACY_REFRESH_TOKEN_KEY = 'bite.kc.refreshToken';
const KC_IS_JWT_SESSION_KEY = 'bite.kc.isJwtSession';
const KC_JWT_DOMAIN_KEY = 'bite.kc.jwtDomain';
const KC_IDP_HINT_KEY = 'bite.kc.idpHint';

/**
 * Keycloak identity-provider aliases are lower-case slugs. Matched rather than
 * trusted, because this value arrives from the query string.
 *
 * The exposure is small by construction: the hint is only ever handed to Keycloak
 * as `kc_idp_hint` and is never reflected into the page or into a redirect URI, and
 * an alias Keycloak does not recognise simply falls through to the normal login
 * form. The pattern exists to keep anything that is not a plausible alias out of
 * the authorization URL at all.
 */
const IDP_HINT_PATTERN = /^[a-z0-9][a-z0-9._-]{0,63}$/i;

const readIdpHintFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    // `idp` is what we publish to customers; `kc_idp_hint` is Keycloak's own
    // parameter name, accepted so a hand-built link works either way.
    const raw = url.searchParams.get('idp') || url.searchParams.get('kc_idp_hint');
    if (!raw) return null;
    const hint = raw.trim();
    return IDP_HINT_PATTERN.test(hint) ? hint : null;
  } catch (error) {
    return null;
  }
};

/**
 * The identity provider this sign-in should be routed to, if any.
 *
 * Held in sessionStorage so a reload or an in-tab navigation after the link has
 * been followed still federates — deliberately NOT localStorage, which would make
 * the redirect permanent for that browser and leave no way back to the BITE login
 * form for someone who later needs it.
 */
const getIdpHint = () => {
  const fromUrl = readIdpHintFromUrl();
  if (fromUrl) {
    try {
      sessionStorage.setItem(KC_IDP_HINT_KEY, fromUrl);
    } catch (error) {
      // Private browsing can refuse storage; the hint still works for this call.
    }
    return fromUrl;
  }
  try {
    return sessionStorage.getItem(KC_IDP_HINT_KEY);
  } catch (error) {
    return null;
  }
};

const cleanTokenFromUrl = () => {
  const url = new URL(window.location.href);
  let changed = false;

  ['jwt', 'session_token', 'sessionToken', 'token', 'key'].forEach((param) => {
    if (url.searchParams.has(param)) {
      url.searchParams.delete(param);
      changed = true;
    }
  });

  if (changed) {
    window.history.replaceState(
      {},
      document.title,
      `${url.pathname}${url.search}${url.hash}`,
    );
  }
};

const getkeyFromRequest = () => {
  const url = new URL(window.location.href);
  return url.searchParams.get('key') || null;
};

const getFallbackFromRequest = () => {
  const url = new URL(window.location.href);
  return url.searchParams.get('fallback') || null;
};

const getJwtTokenFromRequest = () => {
  const url = new URL(window.location.href);
  return (
    url.searchParams.get('jwt') ||
    url.searchParams.get('session_token') ||
    url.searchParams.get('sessionToken') ||
    url.searchParams.get('token')
  );
};

const getStoredJwtToken = () =>
  sessionStorage.getItem(JWT_STORAGE_KEY) ||
  localStorage.getItem(JWT_STORAGE_KEY);
const getStoredJwtDomain = () =>
  sessionStorage.getItem(DOMAIN_STORAGE_KEY) ||
  localStorage.getItem(DOMAIN_STORAGE_KEY);
const getStoredJwtFallback = () =>
  sessionStorage.getItem(DOMAIN_STORAGE_FALLBACK) ||
  localStorage.getItem(DOMAIN_STORAGE_FALLBACK);

const clearStoredJwtSession = () => {
  [JWT_STORAGE_KEY, DOMAIN_STORAGE_KEY, DOMAIN_STORAGE_FALLBACK].forEach(
    (key) => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    },
  );
};

const clearStoredKeycloakSession = () => {
  [
    KC_ACCESS_TOKEN_KEY,
    KC_LEGACY_REFRESH_TOKEN_KEY,
    KC_IS_JWT_SESSION_KEY,
    KC_JWT_DOMAIN_KEY,
  ].forEach((key) => {
    sessionStorage.removeItem(key);
  });
};

const storeKeycloakTokens = (
  tokens,
  { isJwtSession = true, jwtDomain } = {},
) => {
  if (!tokens?.access_token) {
    return;
  }

  sessionStorage.setItem(KC_ACCESS_TOKEN_KEY, tokens.access_token);
  sessionStorage.setItem(KC_IS_JWT_SESSION_KEY, String(isJwtSession));

  if (jwtDomain) {
    sessionStorage.setItem(KC_JWT_DOMAIN_KEY, jwtDomain);
  }
};

const getJwtTokenForLogin = () => {
  const requestToken = getJwtTokenFromRequest();
  const storedToken = getStoredJwtToken();
  const token = requestToken || storedToken;

  const requestDomain = getkeyFromRequest();
  const storedDomain = getStoredJwtDomain();
  const domain = requestDomain || storedDomain;

  const requestFallback = getFallbackFromRequest();
  const storedFallback = getStoredJwtFallback();
  const fallback = requestFallback || storedFallback;

  if (!token) {
    return null;
  }

  if (requestToken) {
    sessionStorage.setItem(JWT_STORAGE_KEY, requestToken);
    sessionStorage.setItem(DOMAIN_STORAGE_KEY, domain);
    sessionStorage.setItem(DOMAIN_STORAGE_FALLBACK, fallback);

    cleanTokenFromUrl();
  }

  return { token: token, domain: domain, fallback: fallback };
};

const getIsWhiteLabelDomain = () => {
  const domains = BITE_DOMAINS ? BITE_DOMAINS.split(/,\s*/) : [''];
  //const domains = [''];
  return domains.indexOf(window.location.host) === -1;
};

const getPostLogoutPath = ({
  brokered,
  isQuickcodeOnly,
  isWhiteLabel,
} = {}) => {
  if (isWhiteLabel) {
    return '/tariff-checker';
  }

  if (brokered === 'quickcode' || isQuickcodeOnly) {
    return '/bite-db';
  }

  return '/';
};

const normalizeLogoutOptions = (options) => {
  if (typeof options === 'string') {
    return {
      brokered: options,
      isQuickcodeOnly: options === 'quickcode',
      isWhiteLabel: getIsWhiteLabelDomain(),
      preserveRecoveryDrafts: false,
    };
  }

  return {
    brokered: options?.brokered,
    isQuickcodeOnly: Boolean(options?.isQuickcodeOnly),
    isWhiteLabel:
      typeof options?.isWhiteLabel === 'boolean'
        ? options.isWhiteLabel
        : getIsWhiteLabelDomain(),
    // Only ever true when axiosHelper's interceptor forces this logout
    // because a request found the session already dead -- see
    // withRecoveryDraftsPreserved in sessionRecovery.ts for why. Every other
    // caller (an explicit, user-initiated sign-out) defaults to false: no
    // leftover draft should survive onto whoever uses the browser next.
    preserveRecoveryDrafts: Boolean(options?.preserveRecoveryDrafts),
  };
};

// You'll need a tiny helper to decode the JWT manually
const decodeToken = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

const isTokenExpired = (tokenParsed, minValiditySeconds = 0) => {
  if (!tokenParsed?.exp) {
    return true;
  }

  return tokenParsed.exp * 1000 <= Date.now() + minValiditySeconds * 1000;
};

/**
 * Distinguishes "the identity provider says this session is over" from "we could
 * not reach the identity provider".
 *
 * keycloak-js throws a NetworkError carrying the Response for HTTP failures and a
 * plain TypeError for transport failures; TenantAPI (axios) also exposes
 * `error.response.status`. Only an explicit auth rejection from the token endpoint
 * means the refresh token is actually dead — a timeout, an offline blip, a
 * sleep/wake, or a 5xx must NOT be treated as a logout.
 */
const isSessionDeadError = (error) =>
  [400, 401, 403].includes(error?.response?.status || error?.status) ||
  /not authenticated|no refresh token available/i.test(error?.message || '');

const extractBrokerTokens = (response) =>
  response?.result?.data?.tokens ||
  response?.result?.result?.data?.tokens ||
  response?.result?.tokens ||
  (response?.result?.access_token ? response.result : null) ||
  response?.data?.tokens ||
  (response?.data?.access_token ? response.data : null) ||
  (response?.access_token ? response : null);

const refreshJwtSessionFromCookie = async () => {
  const response = await TenantAPI.refreshBrokerToken();
  const newTokens = extractBrokerTokens(response);

  if (!newTokens?.access_token) {
    throw new Error('No tokens returned from refresh proxy');
  }

  _kc.token = newTokens.access_token;
  _kc.refreshToken = null;
  _kc.tokenParsed = decodeToken(newTokens.access_token);

  if (!_kc.tokenParsed) {
    throw new Error('Refreshed access token is invalid');
  }

  storeKeycloakTokens(newTokens, { isJwtSession: true });
  return newTokens;
};

/**
 * Keycloak refresh tokens rotate on use: whichever concurrent call reaches the
 * proxy first invalidates the cookie for every other call already in flight.
 * The axios interceptor refreshes on every request and the heartbeat refreshes
 * on its own timer, so without de-duping, two refreshes landing in the same
 * tick made the loser look like a dead session and wiped an active user's
 * login. keycloak-js's own updateToken() already queues concurrent callers
 * this way; this mirrors that for the JWT/partner-broker path.
 */
let jwtRefreshInFlight = null;

const refreshJwtSessionFromCookieDeduped = () => {
  if (!jwtRefreshInFlight) {
    jwtRefreshInFlight = refreshJwtSessionFromCookie().finally(() => {
      jwtRefreshInFlight = null;
    });
  }
  return jwtRefreshInFlight;
};

const bootstrapStoredKeycloakSession = async (onAuthenticatedCallback) => {
  const storedAccess = sessionStorage.getItem(KC_ACCESS_TOKEN_KEY);
  if (!storedAccess) {
    return false;
  }

  const storedIsJwt = sessionStorage.getItem(KC_IS_JWT_SESSION_KEY) === 'true';
  const storedJwtDomain = sessionStorage.getItem(KC_JWT_DOMAIN_KEY);

  try {
    isJwtLoginSession = storedIsJwt;
    _kc.token = storedAccess;
    _kc.refreshToken = null;
    _kc.tokenParsed = decodeToken(storedAccess);
    if (!_kc.tokenParsed) {
      throw new Error('Stored Keycloak access token is invalid');
    }
    if (isTokenExpired(_kc.tokenParsed)) {
      if (storedIsJwt) {
        await refreshJwtSessionFromCookieDeduped();
      } else {
        throw new Error('Stored Keycloak access token is expired');
      }
    }
    _kc.authenticated = true;

    await _kc.init({
      onLoad: undefined,
      checkLoginIframe: false,
      pkceMethod: 'S256',
    });

    const curUser = {
      id: _kc.tokenParsed?.sub,
      name: _kc.tokenParsed?.name,
      email: _kc.tokenParsed?.email,
      resource_access: _kc.tokenParsed?.resource_access ?? {},
      realm_roles: _kc.tokenParsed?.realm_access?.roles ?? [],
      is_jwt_login: isJwtLoginSession,
      jwt_domain: storedJwtDomain,
    };

    startHeartbeat();
    onAuthenticatedCallback(curUser);
    return true;
  } catch (error) {
    clearStoredKeycloakSession();
    isJwtLoginSession = false;
    _kc.clearToken();
    console.warn('Failed to bootstrap stored Keycloak session', error);
    return false;
  }
};

const initKeycloak = async (onAuthenticatedCallback, awsUserTokenPayload) => {
  const restoredSession = await bootstrapStoredKeycloakSession(
    onAuthenticatedCallback,
  );
  if (restoredSession) {
    return;
  }

  if (awsUserTokenPayload) {
    isAWSUserTokenValid = await aWSKeycloakService.isTokenValid(
      awsUserTokenPayload.accessToken,
    );
    if (isAWSUserTokenValid) {
      await aWSKeycloakService.refreshToken(awsUserTokenPayload.refreshToken);
      startHeartbeat();
      onAuthenticatedCallback(aWSKeycloakService.getUser());
    }
    return;
  }

  try {
    const jwtSession = getJwtTokenForLogin();
    let tokens = null;
    //Check session for jwt token
    if (jwtSession && jwtSession.token && jwtSession.domain) {
      try {
        const response = await TenantAPI.brokerLogin(
          jwtSession.token,
          jwtSession.domain,
        );

        tokens = response?.result?.data?.tokens;

        // Trigger redirect if the API succeeds but returns no tokens
        if (!tokens) {
          throw new Error('Broker login returned no tokens.');
        }
        clearStoredJwtSession();
      } catch (error) {
        console.warn(
          'Broker login failed. Redirecting to initiating domain...',
          error,
        );

        // Clean up the session before redirecting
        clearStoredJwtSession();

        // Ensure the domain has a protocol before assigning to window.location.href
        if (jwtSession.fallback) {
          const redirectUrl = jwtSession.fallback.startsWith('http')
            ? jwtSession.fallback
            : `https://${jwtSession.fallback}`;

          window.location.href = redirectUrl;
          return; // Halt further execution of initKeycloak
        }
      }
    }
    //If jwt tokens were parsed then use the logged in user session to populate the keycloak object,
    if (tokens?.access_token) {
      // 1. Manually set the token and parsed payload to skip Keycloak's picky validator
      storeKeycloakTokens(tokens, {
        isJwtSession: true,
        jwtDomain: jwtSession.domain,
      });
      isJwtLoginSession = true;
      _kc.token = tokens.access_token;
      _kc.tokenParsed = decodeToken(tokens.access_token);
      _kc.authenticated = true;
      _kc.refreshToken = null;

      // 2. Initialize with NO LOAD to prevent Keycloak from trying to refresh or check cookies
      await _kc.init({
        onLoad: undefined,
        checkLoginIframe: false,
        pkceMethod: 'S256',
      });

      // 3. Force the callback since we've manually verified the user
      const curUser = {
        id: _kc.tokenParsed?.sub,
        name: _kc.tokenParsed?.name,
        email: _kc.tokenParsed?.email,
        resource_access: _kc.tokenParsed?.resource_access ?? {},
        realm_roles: _kc.tokenParsed?.realm_access?.roles ?? [],
        is_jwt_login: true,
        jwt_domain: jwtSession.domain,
      };

      startHeartbeat();
      onAuthenticatedCallback(curUser);
      return;
    }

    //If no JWT token login then login normally
    const authenticated = await _kc.init({
      onLoad: 'check-sso',
      checkLoginIframe: false,
      pkceMethod: 'S256',
      // keycloak-js logs "[KEYCLOAK] Estimated time difference..." and
      // "[KEYCLOAK] Token expires in Ns" on every refresh when this is on.
      // Diagnostic only — flip to true locally when debugging token/clock issues.
      enableLogging: false,
    });

    if (authenticated) {
      const curUser = {
        id: _kc.tokenParsed?.sub,
        name: _kc.tokenParsed?.name,
        email: _kc.tokenParsed?.email,
        resource_access: _kc.tokenParsed?.resource_access ?? {},
        realm_roles: _kc.tokenParsed?.realm_access?.roles ?? [],
      };
      startHeartbeat();
      onAuthenticatedCallback(curUser);
      return;
    }
    doLogin();
  } catch (e) {
    console.error('Failed to initialize Keycloak', e);
    throw e;
  }
};

const doLogin = async () => {
  const domains = BITE_DOMAINS ? BITE_DOMAINS.split(/,\s*/) : [];
  const currentHost = window.location.host;
  const isNotBiteDomain = domains.indexOf(currentHost) === -1;

  if (isNotBiteDomain) {
    // A tenant on its own hostname: the Tenant row for this host names the IdP.
    const idpConfigSecret = await TenantAPI.getIDP();
    await _kc.login({
      idpHint: idpConfigSecret?.result?.data ? idpConfigSecret.result.data : '',
      redirectUri: window.location.origin,
      // prompt: 'login',
    });
    return;
  }

  // A shared BITE host. A federated customer arrives here from a link in their own
  // portal carrying ?idp=<alias>, and that is the only routing signal available at
  // this point — there is no user, email or session yet to infer one from, which is
  // why the host is otherwise the only thing this function can branch on.
  //
  // Absent a hint we fall through to the BITE login form, which is what every
  // ordinary user gets.
  const idpHint = getIdpHint();
  const redirectUri = getBiteLoginRedirectUri(
    new URL(window.location.href),
  );
  if (idpHint) {
    await _kc.login({
      idpHint: idpHint,
      redirectUri: redirectUri,
    });
    return;
  }

  // Standard BITE sign-ins must always complete on the homepage. Keycloak's
  // default is to preserve the current deep link, which lets registration
  // callbacks re-enter a feature route before first-login onboarding begins.
  // Existing authenticated sessions still retain deep links because doLogin is
  // only reached when check-sso reports that there is no active session.
  await _kc.login({ redirectUri: redirectUri });
};

const doLogout = async (options) => {
  const logoutOptions = normalizeLogoutOptions(options);
  const postLogoutPath = getPostLogoutPath(logoutOptions);
  const redirectUri = `${window.location.origin}${postLogoutPath}`;
  const shouldClearPartnerCookie = isJwtLoginSession;

  stopHeartbeat();
  clearStoredKeycloakSession();
  isJwtLoginSession = false;

  if (isAWSUserTokenValid) {
    aWSKeycloakService.doLogout(redirectUri);
    return;
  }

  if (shouldClearPartnerCookie) {
    try {
      await TenantAPI.clearBrokerRefreshToken();
    } catch (error) {
      console.warn('Failed to clear partner SSO refresh cookie', error);
    }
  }

  sessionStorage.clear();
  if (logoutOptions.preserveRecoveryDrafts) {
    withRecoveryDraftsPreserved(() => localStorage.clear());
    // This redirect is forced by a dead session, not chosen by the user, and
    // captureRecoverableState() has already snapshotted whatever they were
    // working on -- so a page's own "unsaved changes" beforeunload guard has
    // nothing left to protect here and shouldn't prompt for this navigation.
    // See sessionRecovery.ts's isForcedNavigationInFlight() doc for the full
    // story (this is what was causing the Tariff Checker's double "Leave
    // site?" dialog: one real prompt for the user's own attempt to leave,
    // then a second for this redirect moments later).
    markForcedNavigation();
  } else {
    localStorage.clear();
  }

  _kc.logout({ redirectUri: redirectUri });
};

const getToken = () =>
  isAWSUserTokenValid ? aWSKeycloakService.getToken() : _kc.token;

const getUser = () => {
  if (isAWSUserTokenValid) {
    return aWSKeycloakService.getUser();
  }
  return {
    id: _kc.tokenParsed?.sub,
    name: _kc.tokenParsed?.name,
    email: _kc.tokenParsed?.email,
    resource_access: _kc.tokenParsed?.resource_access,
    realm_roles: _kc.tokenParsed?.realm_access?.roles ?? [],
  };
};

const getUserById = async (user_id) => {
  await _kc.users?.findOne({ id: user_id });
};

const isLoggedIn = () =>
  isAWSUserTokenValid ? Boolean(isAWSUserTokenValid) : Boolean(_kc.token);

const updateToken = async (successCallback) => {
  const onSuccess =
    typeof successCallback === 'function' ? successCallback : null;
  const minValidity =
    typeof successCallback === 'number' ? successCallback : 60;
  const session = getStoredJwtToken();
  if (session && !isAWSUserTokenValid) {
    return false;
  }
  if (isAWSUserTokenValid) {
    const forceRefresh = minValidity === -1;
    if (!forceRefresh && !aWSKeycloakService.isTokenExpired(minValidity)) {
      if (onSuccess) onSuccess(false);
      return false;
    }

    try {
      await aWSKeycloakService.refreshToken();
      if (onSuccess) onSuccess(true);
      return true;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  if (isJwtLoginSession) {
    // Mirror keycloak-js's contract, where minValidity === -1 means "refresh
    // unconditionally". isTokenExpired() has no such special case — it treats -1
    // as a *negative* validity window, so it only reports expiry a full second
    // AFTER the token has already died. Without this branch, updateToken(-1)
    // refreshes a brokered session less eagerly than the plain default does, and
    // the heartbeat never reaches refreshJwtSessionFromCookie() at all.
    const forceRefresh = minValidity === -1;
    if (!forceRefresh && !isTokenExpired(_kc.tokenParsed, minValidity)) {
      if (onSuccess) onSuccess(false);
      return false;
    }

    try {
      await refreshJwtSessionFromCookieDeduped();

      if (onSuccess) onSuccess(true);
      return true;
    } catch (error) {
      // Only tear the session down when the proxy actually rejects us. Wiping it
      // on a transient failure logs out a user who is sitting there working.
      if (isSessionDeadError(error)) {
        console.error('Broker refresh rejected; session is over.', error);
        clearStoredKeycloakSession();
        _kc.clearToken();
        isJwtLoginSession = false;
      } else {
        console.warn(
          'Transient broker refresh failure — session left intact.',
          error,
        );
      }
      throw error;
    }
  }

  try {
    const refreshed = await _kc.updateToken(minValidity);
    if (onSuccess) onSuccess(refreshed);
    return refreshed;
  } catch (error) {
    // Do NOT clear unconditionally. keycloak-js already clears the token itself
    // when Keycloak definitively rejects the refresh (HTTP 400 -> invalid_grant;
    // see updateToken in keycloak-js/lib/keycloak.js). Clearing here as well wiped
    // _kc.token on *any* failure — one offline blip, sleep/wake, or 5xx made
    // isLoggedIn() false, which made the next API call log the user out with no
    // recovery short of a page reload. That defeated onTokenExpired's explicit
    // "leaving session intact" intent.
    if (isSessionDeadError(error)) {
      console.error('Keycloak rejected the refresh token; session is over.', error);
    } else {
      console.warn(
        'Transient token refresh failure — session left intact.',
        error,
      );
    }
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Activity heartbeat
//
// Keeps Keycloak's session-idle clock alive while the user is working in the UI
// but not necessarily calling the API (reading a report, filling a form). The
// axios interceptor only refreshes on request, so without this a busy-but-quiet
// user still idles out.
//
// Note this cannot defeat an *absolute* cap: if the realm's Client Session Max /
// SSO Session Max elapses, the refresh token dies no matter how active the user is.
// ---------------------------------------------------------------------------

/** How recently the user must have interacted to count as "actively using". */
const ACTIVITY_WINDOW_MS = 10 * 60 * 1000;
/** Sample far more often than the window so activity is never missed between ticks. */
const HEARTBEAT_TICK_MS = 60 * 1000;
/** Refresh once the access token is this far through its lifetime. */
const REFRESH_AFTER_RATIO = 0.5;
/** Floor between refresh attempts, so an odd token shape can't cause a hot loop. */
const MIN_REFRESH_SPACING_MS = 60 * 1000;

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll'];

let lastActivityTime = Date.now();
let heartbeatInterval = null;
let lastRefreshAttempt = 0;

const recordActivity = () => {
  lastActivityTime = Date.now();
};

const isRecentlyActive = () => Date.now() - lastActivityTime < ACTIVITY_WINDOW_MS;

/**
 * Refresh on a fraction of the token's real lifetime rather than a fixed
 * minValidity, so the cadence stays correct whatever the realm's Access Token
 * Lifespan is set to.
 */
const shouldProactivelyRefresh = () => {
  // Decode the token returned by the active auth adapter. AWS sessions are not
  // represented by _kc.tokenParsed, which previously made them force-refresh
  // every minute regardless of their real token lifetime.
  const parsed = decodeToken(getToken());
  if (!parsed?.exp) return true;

  const expMs = parsed.exp * 1000;
  const iatMs = typeof parsed.iat === 'number' ? parsed.iat * 1000 : null;

  // No issue time to measure against — fall back to "close to expiry".
  if (iatMs === null) return expMs - Date.now() <= 2 * 60 * 1000;

  const lifetimeMs = expMs - iatMs;
  if (lifetimeMs <= 0) return true;

  return Date.now() - iatMs >= lifetimeMs * REFRESH_AFTER_RATIO;
};

const heartbeatTick = () => {
  if (!isLoggedIn() || !isRecentlyActive()) return;
  if (!shouldProactivelyRefresh()) return;
  if (Date.now() - lastRefreshAttempt < MIN_REFRESH_SPACING_MS) return;

  lastRefreshAttempt = Date.now();

  // -1 forces the refresh-token exchange. The previous call passed no argument,
  // which resolved to minValidity=60 — so keycloak-js returned false without ever
  // contacting Keycloak unless the token happened to be within 60s of expiring.
  // The heartbeat detected activity and then did nothing with it.
  updateToken(-1).catch((error) => {
    if (isSessionDeadError(error)) {
      console.warn('Heartbeat: session is no longer valid.', error);
      return;
    }
    console.warn('Heartbeat refresh failed (transient) — retrying next tick.', error);
  });
};

const handleVisibilityChange = () => {
  if (document.visibilityState !== 'visible') return;
  // Background tabs get their timers throttled and sleeping machines stop firing
  // them entirely, so ticks are routinely missed while hidden. Deliberately
  // switching back to the app is itself interaction, so record it and re-check
  // now rather than waiting up to a full tick.
  recordActivity();
  heartbeatTick();
};

const startHeartbeat = () => {
  if (heartbeatInterval) clearInterval(heartbeatInterval);

  // Same function references, so repeat calls are de-duplicated by the browser.
  ACTIVITY_EVENTS.forEach((event) =>
    window.addEventListener(event, recordActivity, { passive: true }),
  );
  document.addEventListener('visibilitychange', handleVisibilityChange);

  recordActivity();
  lastRefreshAttempt = 0;
  heartbeatInterval = setInterval(heartbeatTick, HEARTBEAT_TICK_MS);
};

const stopHeartbeat = () => {
  if (heartbeatInterval) clearInterval(heartbeatInterval);
  heartbeatInterval = null;

  ACTIVITY_EVENTS.forEach((event) =>
    window.removeEventListener(event, recordActivity),
  );
  document.removeEventListener('visibilitychange', handleVisibilityChange);
};

const getUsername = () =>
  isAWSUserTokenValid
    ? aWSKeycloakService.getUsername()
    : _kc.tokenParsed?.preferred_username;

const isBiteAdmin = () =>
  isAWSUserTokenValid
    ? aWSKeycloakService.isBiteAdmin()
    : _kc.tokenParsed?.realm_access?.roles?.includes('bite-admin');

const isServiceAccount = () =>
  isAWSUserTokenValid
    ? aWSKeycloakService.isServiceAccount()
    : _kc.tokenParsed?.realm_access?.roles?.includes('service-account');

const hasRole = (roles) =>
  isAWSUserTokenValid
    ? aWSKeycloakService.hasRealmRoles(roles)
    : roles.some((role) => _kc.hasRealmRole(role));

const KeycloakService = {
  initKeycloak: initKeycloak,
  doLogin: doLogin,
  doLogout: doLogout,
  isLoggedIn: isLoggedIn,
  getToken: getToken,
  updateToken: updateToken,
  getUsername: getUsername,
  getUser: getUser,
  getUserById: getUserById,
  hasRole: hasRole,
  isBiteAdmin: isBiteAdmin,
  isServiceAccount: isServiceAccount,
  startHeartbeat: startHeartbeat,
  stopHeartbeat: stopHeartbeat,
  isSessionDeadError: isSessionDeadError,
};

export default KeycloakService;
